import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IIndicatorState } from './Indicators.interface';
import {
  IGETIndicatorsResponse,
  IIndicatorCreate,
} from 'src/services/dashboard/indicator/indicator.interface';
import {
  createIndicator,
  getIndicator,
  getIndicators,
} from 'src/services/dashboard/indicator/indicator.service';

/**
 * The initial state for the Indicator slice.
 *
 * @constant
 * @type {IIndicatorState}
 */
const initialState: IIndicatorState = {
  loading: false,
  indicators: [],
  indicator: {
    indicatorId: 0,
    areaId: 0,
    channelId: 0,
    indicatorName: '',
    indicatorLabelOne: '',
    indicatorLabelTwo: '',
    indicatorDescription: '',
    areaName: '',
    channelName: '',
    indicatorActive: false,
    strategies: [],
  },
  indicatorCreate: {
    areaId: null,
    channelId: null,
    indicatorName: '',
    indicatorLabelOne: '',
    indicatorLabelTwo: '',
    indicatorDescription: '',
    strategies: [],
  },
  filter: {
    areaName: null,
    channelId: null,
    indicatorName: null,
    periodId: null,
    quarterId: null,
  },
  responseLoadIndicator: null,
};

/**
 * A slice of the Redux store responsible for managing indicator data.
 *
 * This slice contains actions and reducers to control the loading state,
 * manage indicators, and handle API responses for creating and retrieving indicators.
 *
 * @constant
 * @type {Slice<IIndicatorState>}
 */
const StrategySlice = createSlice({
  name: 'KPIForm',
  initialState,
  reducers: {
    /**
     * Sets the filter based on the provided payload.
     *
     * @param {IIndicatorState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IIndicatorState['filter']>} action - The action containing the new filter values.
     */
    setFilter: (state, action: PayloadAction<IIndicatorState['filter']>) => {
      state.filter = action.payload;
    },

    /**
     * Sets the current indicator based on the provided payload.
     *
     * @param {IIndicatorState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IIndicatorState['indicator']>} action - The action containing the new indicator data.
     */
    setIndicator: (
      state,
      action: PayloadAction<IIndicatorState['indicator']>,
    ) => {
      state.indicator = action.payload;
    },

    /**
     * Sets the data for creating a new indicator based on the provided payload.
     *
     * @param {IIndicatorState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IIndicatorState['indicatorCreate']>} action - The action containing the new indicator creation data.
     */
    setIndicatorCreate: (
      state,
      action: PayloadAction<IIndicatorState['indicatorCreate']>,
    ) => {
      state.indicatorCreate = action.payload;
    },

    /**
     * Resets the filters to the initial state.
     *
     * @param {IIndicatorState} state - The current state of the KPIForm slice.
     */
    resetFilters: (state) => {
      state.filter = initialState.filter;
    },

    /**
     * Sets the response for loading an indicator.
     *
     * @param {IIndicatorState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IIndicatorState['responseLoadIndicator']>} action - The action containing the response data.
     */
    setResponseLoadIndicator: (
      state,
      action: PayloadAction<IIndicatorState['responseLoadIndicator']>,
    ) => {
      state.responseLoadIndicator = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        createIndicator.fulfilled,
        (state, action: PayloadAction<IIndicatorCreate[]>) => {
          state.loading = false;
          state.indicatorCreate = {
            areaId: 0,
            channelId: 0,
            indicatorName: '',
            indicatorLabelOne: '',
            indicatorLabelTwo: '',
            indicatorDescription: '',
            strategies: [],
          };
        },
      )
      .addCase(
        getIndicators.fulfilled,
        (state, action: PayloadAction<IGETIndicatorsResponse[]>) => {
          state.loading = false;
          state.indicators = action.payload;
        },
      )
      .addCase(getIndicator.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getIndicator.fulfilled,
        (
          state,
          action: PayloadAction<IIndicatorState['responseLoadIndicator']>,
        ) => {
          state.loading = false;
          state.responseLoadIndicator = action.payload;
          console.log(state.responseLoadIndicator, 'state.indicator');
        },
      );
  },
});

/**
 * Action creators generated from the KPIForm slice.
 *
 * @constant
 * @type {{ setFilter: (filter: IIndicatorState['filter']) => PayloadAction<IIndicatorState['filter']>, setIndicator: (indicator: IIndicatorState['indicator']) => PayloadAction<IIndicatorState['indicator']>, setIndicatorCreate: (indicatorCreate: IIndicatorState['indicatorCreate']) => PayloadAction<IIndicatorState['indicatorCreate']>, resetFilters: () => PayloadAction<void>, setResponseLoadIndicator: (response: IIndicatorState['responseLoadIndicator']) => PayloadAction<IIndicatorState['responseLoadIndicator']> }}
 */
export const {
  setFilter,
  setIndicatorCreate,
  resetFilters,
  setIndicator,
  setResponseLoadIndicator,
} = StrategySlice.actions;

/**
 * The reducer for the KPIForm slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<IIndicatorState>}
 */
export default StrategySlice.reducer;
